import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Heart, X, Bookmark, Link2, Download, ChevronLeft, ChevronRight, Repeat, RefreshCw } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MoireSymbol } from "@/components/MoireLogo";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import { assetForItem } from "@/lib/categoryAssets";

const clone = (o) => JSON.parse(JSON.stringify(o));
// Visual hierarchy: real user/wardrobe photo → curated category asset → colour swatch.
const pieceSrc = (item) => (item?.image_url ? imageUrl(item.image_url) : assetForItem(item));
const SLOT_LABEL = { tops: "Top", bottoms: "Bottom", dresses: "Dress", outerwear: "Layer", shoes: "Shoes",
  belt: "Belt", watch: "Watch", sunglasses: "Sunglasses", necklace: "Jewellery", bag: "Bag" };

const STYLE_UP = [
  { label: "More Casual", key: "relaxed" }, { label: "More Elevated", key: "elevated" },
  { label: "Bolder", key: "statement" }, { label: "More Minimal", key: "classic" },
];

const VIZ_STAGES = ["Building the outfit…", "Finishing the details…", "Styling the complete look…"];

// Client-side outfit signature — mirrors the backend cache key (presentation + pieces).
const sigOf = (comp, pres) => JSON.stringify({
  p: pres || "auto",
  g: (comp.garments || []).map((g) => [g.slot || g.group, g.name, g.color_hex, g.pattern || "solid"]),
  a: (comp.accessories || []).map((a) => [a.slot || a.category, a.name, a.color_hex]),
});

export default function MoireLook() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [active, setActive] = useState(null);      // working composition {garments, accessories, key, name, ...}
  const [locks, setLocks] = useState({});          // {slot: piece} kept across directions
  const [score, setScore] = useState(null);
  const [why, setWhy] = useState("");
  const [swap, setSwap] = useState(null);          // {kind, slot}
  const [alts, setAlts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareId, setShareId] = useState(null);
  const cardRef = useRef(null);

  // ---- worn-look visualisation state ----
  const [viz, setViz] = useState({ status: "idle", image_url: null, presentation: null, disclaimer: "" });
  const [presentation, setPresentation] = useState(null);   // masculine | feminine (resolved after first render)
  const [stale, setStale] = useState(false);                // image reflects a previous composition
  const vizMapRef = useRef(new Map());                      // sig -> {image_url, presentation, disclaimer}
  const presRef = useRef(null);
  useEffect(() => { presRef.current = presentation; }, [presentation]);

  useEffect(() => {
    let req = null;
    try { req = JSON.parse(sessionStorage.getItem("moire.lookRequest") || "null"); } catch { /* noop */ }
    if (!req && window.location.hash.length > 1) {
      try {
        const bin = atob(window.location.hash.slice(1));
        const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
        req = JSON.parse(new TextDecoder().decode(bytes));
      } catch { /* noop */ }
    }
    if (!req) { navigate("/match"); return; }
    setRequest(req);
    buildLook(req);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildLook = async (req) => {
    setData(null);
    try {
      const { data: d } = await api.post("/look/build", req);
      if (!d.looks?.length) { toast.error("MOIRÉ couldn't compose that look."); navigate(-1); return; }
      setData(d);
      loadDirection(d.looks, 0, {}, true);
    } catch { toast.error("Couldn't build the look. Try again."); navigate(-1); }
  };

  const applyLocks = (comp, lockMap) => {
    const c = clone(comp);
    Object.entries(lockMap).forEach(([slot, piece]) => {
      const gi = c.garments.findIndex((g) => g.slot === slot);
      if (gi >= 0) c.garments[gi] = piece;
      const ai = c.accessories.findIndex((a) => a.slot === slot);
      if (ai >= 0) c.accessories[ai] = piece;
    });
    return c;
  };

  const loadDirection = async (looks, idx, lockMap, auto = true) => {
    const comp = applyLocks(looks[idx], lockMap ?? locks);
    setActiveIdx(idx); setActive(comp); setScore(comp.score); setWhy(comp.why);
    recalc(comp);
    syncViz(comp, auto);   // generate worn look for this direction (cached after first time)
  };

  const recalc = async (comp) => {
    try {
      const { data: r } = await api.post("/look/rescore", {
        garments: comp.garments, accessories: comp.accessories,
        occasion: data?.occasion ?? request?.occasion ?? null, direction: comp.key });
      setScore(r.score); setWhy(r.why);
    } catch { /* keep previous */ }
  };

  // ---- worn-look generation ----
  const syncViz = (comp, auto) => {
    const pres = presRef.current;
    const cached = vizMapRef.current.get(sigOf(comp, pres)) || (pres ? null : firstCached(comp));
    if (cached) {
      setViz({ status: "ready", image_url: cached.image_url, presentation: cached.presentation, disclaimer: cached.disclaimer });
      if (!pres && cached.presentation) setPresentation(cached.presentation);
      setStale(false);
      return;
    }
    if (auto) generateViz(comp, pres);
    else setStale(true);   // composition changed by a swap — wait for "Update Visualisation"
  };

  const firstCached = (comp) => {
    for (const p of ["masculine", "feminine"]) {
      const c = vizMapRef.current.get(sigOf(comp, p));
      if (c) return c;
    }
    return null;
  };

  const generateViz = async (comp, pres) => {
    setViz((v) => ({ ...v, status: "loading" })); setStale(false);
    try {
      const { data: r } = await api.post("/look/visualise", {
        direction: comp.key, name: comp.name,
        occasion: data?.occasion ?? request?.occasion ?? null,
        garments: comp.garments, accessories: comp.accessories, presentation: pres });
      if (r.available && r.image_url) {
        const entry = { image_url: r.image_url, presentation: r.presentation, disclaimer: r.disclaimer };
        vizMapRef.current.set(sigOf(comp, r.presentation), entry);
        vizMapRef.current.set(sigOf(comp, pres), entry);
        if (!presRef.current) setPresentation(r.presentation);
        setViz({ status: "ready", ...entry }); setStale(false);
      } else {
        setViz({ status: "unavailable", image_url: null, presentation: pres, disclaimer: r.disclaimer });
        setStale(false);
      }
    } catch {
      setViz({ status: "unavailable", image_url: null, presentation: pres,
        disclaimer: "The visual preview isn't available right now, but your complete styling board is ready." });
    }
  };

  const updateVisualisation = () => generateViz(active, presRef.current);

  const togglePresentation = (p) => {
    if (p === presentation) return;
    setPresentation(p);
    generateViz(active, p);
  };

  const downloadViz = async () => {
    if (!viz.image_url) return;
    try {
      const res = await fetch(imageUrl(viz.image_url));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "moire-look.png"; a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Couldn't download the image."); }
  };

  const openSwap = async (kind, slot) => {
    setSwap({ kind, slot }); setAlts("loading");
    try {
      const { data: r } = await api.post("/look/alternatives", {
        kind, slot, garments: active.garments, accessories: active.accessories,
        occasion: data?.occasion ?? null, direction: active.key });
      setAlts(r.alternatives || []);
    } catch { setAlts([]); }
  };

  const applyAlt = (alt) => {
    const c = clone(active);
    const piece = { ...alt, slot: swap.slot };
    if (swap.kind === "garment") {
      const gi = c.garments.findIndex((g) => g.slot === swap.slot);
      if (gi >= 0) c.garments[gi] = { ...c.garments[gi], ...piece, kind: "garment" };
    } else {
      const ai = c.accessories.findIndex((a) => a.slot === swap.slot);
      if (ai >= 0) c.accessories[ai] = { ...c.accessories[ai], ...piece, kind: "accessory" };
    }
    setActive(c);
    if (locks[swap.slot]) setLocks({ ...locks, [swap.slot]: piece });
    setSwap(null); setAlts(null); recalc(c);
    syncViz(c, false);   // mark stale → "Update Visualisation"
  };

  const toggleKeep = (item) => {
    const nl = { ...locks };
    if (nl[item.slot]) delete nl[item.slot]; else nl[item.slot] = item;
    setLocks(nl);
  };

  const removeAccessory = (slot) => {
    const c = clone(active);
    c.accessories = c.accessories.filter((a) => a.slot !== slot);
    const nl = { ...locks }; delete nl[slot]; setLocks(nl);
    setActive(c); recalc(c);
    syncViz(c, false);
  };

  const styleIt = (key) => {
    if (!data) return;
    const idx = data.looks.findIndex((l) => l.key === key);
    if (idx >= 0) loadDirection(data.looks, idx, locks, true);
    else toast("That direction isn't available for this look.");
  };

  const restyle = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/look/build", request);
      setData(d);
      loadDirection(d.looks, Math.min(activeIdx, d.looks.length - 1), locks, true);
      toast.success("Restyled — locked pieces kept.");
    } catch { toast.error("Couldn't restyle."); } finally { setBusy(false); }
  };

  const save = async () => {
    if (!user) { toast("Sign in to save your look."); navigate("/signin"); return; }
    try {
      await api.post("/look/save", { name: active.name, occasion: data?.occasion, direction: active.key,
        score, why, garments: active.garments, accessories: active.accessories, source: request });
      toast.success("Look saved.");
    } catch { toast.error("Couldn't save the look."); }
  };

  const shareLink = async () => {
    try {
      const { data: r } = await api.post("/look/share", { name: active.name, occasion: data?.occasion,
        direction: active.key, score, why, garments: active.garments, accessories: active.accessories });
      const url = `${window.location.origin}/look/shared/${r.share_id}`;
      setShareId(r.share_id);
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied.");
    } catch { toast.error("Couldn't create a share link."); }
  };

  const shareCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0D0D0D" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "moire-look.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "MOIRÉ Look" });
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "moire-look.png"; a.click();
        toast.success("Look card downloaded.");
      }
    } catch { toast.error("Couldn't create the card."); }
  };

  const allItems = useMemo(() => active ? [...active.garments, ...active.accessories] : [], [active]);

  if (!data || !active) {
    return <div className="max-w-3xl mx-auto px-5 py-24 min-h-[60vh]">
      <AnalyzingSequence stages={["Pulling the pieces together…", "Coordinating colours…", "Styling the finish…"]} /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16 min-h-[70vh]">
      {/* Header + direction number nav */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">MOIRÉ Look · Here's how I'd wear it</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#F5F2EA] mt-1">{active.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { const n = Math.max(0, activeIdx - 1); loadDirection(data.looks, n, locks, true); }}
            className="p-2 border border-[#F5F2EA]/15 hover:border-[#C8A97E] transition-colors" data-testid="look-prev"><ChevronLeft size={16} className="text-[#F5F2EA]" /></button>
          <span className="font-serif text-[#C8A97E] text-2xl w-16 text-center">{String(activeIdx + 1).padStart(2, "0")}<span className="text-[#8A8170] text-sm">/{String(data.looks.length).padStart(2, "0")}</span></span>
          <button onClick={() => { const n = Math.min(data.looks.length - 1, activeIdx + 1); loadDirection(data.looks, n, locks, true); }}
            className="p-2 border border-[#F5F2EA]/15 hover:border-[#C8A97E] transition-colors" data-testid="look-next"><ChevronRight size={16} className="text-[#F5F2EA]" /></button>
        </div>
      </div>

      {/* Style directions (other ways to wear it) */}
      <div className="flex gap-2 overflow-x-auto mt-5 pb-1 -mx-1 px-1" data-testid="direction-carousel">
        {data.looks.map((l, i) => (
          <button key={l.key} onClick={() => loadDirection(data.looks, i, locks, true)} data-testid={`direction-${l.key}`}
            className={`shrink-0 px-4 py-2.5 font-sans text-[10px] tracking-luxe uppercase border transition-colors ${i === activeIdx ? "border-[#C8A97E] bg-[#111111] text-[#F5F2EA]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}>
            <span className="text-[#C8A97E] mr-2">{String(i + 1).padStart(2, "0")}</span>{l.name}
          </button>
        ))}
      </div>

      {request?.beforeAfter && (
        <div className="border border-[#C8A97E]/40 bg-[#111111] p-4 mt-6 flex items-center justify-between" data-testid="before-after">
          <div className="text-center flex-1">
            <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">Your Look</p>
            <p className="font-serif text-3xl text-[#F5F2EA]/70">{Number(request.beforeAfter.your_score).toFixed(1)}</p>
          </div>
          <ChevronRight size={20} className="text-[#C8A97E]" />
          <div className="text-center flex-1">
            <p className="font-sans text-[9px] tracking-luxe uppercase text-[#C8A97E]">MOIRÉ's Edit</p>
            <p className="font-serif text-3xl text-[#C8A97E]">{Number(request.beforeAfter.edit_score).toFixed(1)}</p>
          </div>
          {request.beforeAfter.key_change && (
            <div className="flex-[2] pl-4 border-l border-[#F5F2EA]/10">
              <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">The change</p>
              <p className="font-serif text-sm text-[#F5F2EA]">
                {request.beforeAfter.key_change.removed
                  ? `Removed the ${request.beforeAfter.key_change.from}`
                  : `${request.beforeAfter.key_change.from} → ${request.beforeAfter.key_change.to}`}</p>
            </div>
          )}
        </div>
      )}

      {/* HERO: worn look (left) + MOIRÉ's take (right) */}
      <div className="grid lg:grid-cols-[1fr_1fr] gap-8 mt-8">
        <div className="order-1">
          <WornLook viz={viz} stale={stale} presentation={presentation}
            onUpdate={updateVisualisation} onToggle={togglePresentation} onDownload={downloadViz} />
        </div>

        <div className="order-2">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ Look Score</p>
              <div className="font-serif text-5xl text-[#C8A97E]" data-testid="look-score">{Number(score).toFixed(1)}<span className="text-xl text-[#8A8170]"> / 10</span></div>
            </div>
            {score >= 9.0 && <span className="font-serif italic text-2xl text-[#C8A97E]">CLOCK IT 🤏🏼</span>}
          </div>
          <p className="font-serif italic text-lg text-[#F5F2EA]/85 mt-4 leading-relaxed" data-testid="look-why">{why}</p>
          {active.restraint?.length > 0 && (
            <p className="font-sans text-xs text-[#8A8170] mt-2">{active.restraint[0]}</p>
          )}

          <div className="mt-6 divide-y divide-[#F5F2EA]/8 border-t border-[#F5F2EA]/8" data-testid="look-items">
            {allItems.map((it, i) => (
              <div key={it.slot + i} className="flex items-center gap-3 py-3">
                <span className="h-9 w-9 shrink-0 border border-[#F5F2EA]/15 flex items-center justify-center overflow-hidden bg-[#F5F2EA]" style={!pieceSrc(it) ? { backgroundColor: it.color_hex } : {}}>
                  {pieceSrc(it) && <img src={pieceSrc(it)} alt="" loading="lazy" className="w-full h-full object-cover" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#F5F2EA] leading-tight truncate">{it.name}</div>
                  <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">
                    {SLOT_LABEL[it.slot] || it.slot} · {it.source === "wardrobe" ? "From your wardrobe" : it.source === "start" ? "Your piece" : "MOIRÉ suggests"}
                  </div>
                </div>
                <button onClick={() => openSwap(it.kind, it.slot)} data-testid={`item-swap-${it.slot}`}
                  className="font-sans text-[9px] tracking-luxe uppercase text-[#C8A97E] hover:text-[#E5C293]">Swap</button>
                <button onClick={() => toggleKeep(it)} data-testid={`item-keep-${it.slot}`} title="Keep">
                  <Heart size={14} className={locks[it.slot] ? "text-[#C8A97E] fill-[#C8A97E]" : "text-[#8A8170] hover:text-[#F5F2EA]"} /></button>
                {it.removable && it.kind === "accessory" && (
                  <button onClick={() => removeAccessory(it.slot)} data-testid={`item-remove-${it.slot}`} title="Remove"><X size={14} className="text-[#8A8170] hover:text-[#F5F2EA]" /></button>
                )}
              </div>
            ))}
          </div>

          {/* style-up */}
          <div className="mt-6">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-2">Style it…</p>
            <div className="flex flex-wrap gap-2">
              {STYLE_UP.map((s) => (
                <button key={s.key} onClick={() => styleIt(s.key)} data-testid={`styleup-${s.key}`}
                  className="font-sans text-[10px] tracking-luxe uppercase border border-[#F5F2EA]/12 px-3 py-2 text-[#F5F2EA]/80 hover:border-[#C8A97E] transition-colors">{s.label}</button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 mt-7">
            <button onClick={save} data-testid="look-save" className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><Bookmark size={14} strokeWidth={1.5} /> Save Look</button>
            <button onClick={shareCard} data-testid="look-share-card" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors"><Download size={14} strokeWidth={1.5} /> Share Card</button>
            <button onClick={shareLink} data-testid="look-share-link" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors"><Link2 size={14} strokeWidth={1.5} /> Copy Link</button>
            <button onClick={restyle} disabled={busy} data-testid="look-restyle" className="flex items-center gap-2 border border-[#C8A97E]/60 text-[#C8A97E] px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors"><Repeat size={14} strokeWidth={1.5} /> Restyle the Rest</button>
          </div>
        </div>
      </div>

      {/* SECONDARY: interactive stylist board — edit the pieces */}
      <div className="mt-12 border-t border-[#F5F2EA]/8 pt-8" data-testid="look-board-section">
        <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">The Style Board · Edit the pieces</p>
        <p className="font-sans text-[11px] text-[#8A8170] mt-1">Tap any piece to try another, keep the ones you love, then Update the visualisation.</p>
        <div className="border border-[#F5F2EA]/10 bg-gradient-to-br from-[#141414] to-[#0F0F0F] p-4 md:p-6 mt-4" data-testid="look-board">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 auto-rows-[120px] md:auto-rows-[150px]">
            {active.garments.map((g, i) => (
              <BoardTile key={g.slot + i} item={g} big={i === 0} kept={!!locks[g.slot]}
                onSwap={() => openSwap("garment", g.slot)} onKeep={() => toggleKeep(g)} />
            ))}
          </div>
          {active.accessories.length > 0 && (
            <div className="mt-3">
              <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mb-2">The Details</p>
              <div className="flex flex-wrap gap-2.5">
                {active.accessories.map((a, i) => (
                  <AccTile key={a.slot + i} item={a} kept={!!locks[a.slot]}
                    onSwap={() => openSwap("accessory", a.slot)} onKeep={() => toggleKeep(a)}
                    onRemove={() => removeAccessory(a.slot)} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* swap sheet */}
      <AnimatePresence>
        {swap && (
          <div className="fixed inset-0 z-50 bg-[#0D0D0D]/80 flex items-end md:items-center justify-center p-0 md:p-5" onClick={() => setSwap(null)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="bg-[#0D0D0D] border border-[#C8A97E]/40 w-full md:max-w-sm p-6 rounded-t-2xl md:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="look-swap-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-[#F5F2EA]">Try another {SLOT_LABEL[swap.slot]?.toLowerCase() || "piece"}</h3>
                <button onClick={() => setSwap(null)}><X size={18} className="text-[#8A8170]" /></button>
              </div>
              <div className="space-y-2 mt-4 max-h-[55vh] overflow-y-auto">
                {alts === "loading" && <p className="font-sans text-sm text-[#8A8170]">Finding options…</p>}
                {Array.isArray(alts) && alts.length === 0 && <p className="font-sans text-sm text-[#8A8170]">No alternatives right now.</p>}
                {Array.isArray(alts) && alts.map((alt, i) => (
                  <button key={i} onClick={() => applyAlt(alt)} data-testid={`look-alt-${i}`}
                    className="w-full flex items-center gap-3 border border-[#F5F2EA]/10 p-2.5 hover:border-[#C8A97E] transition-colors text-left">
                    <span className="font-serif text-[#C8A97E] text-sm w-6">{String(i + 1).padStart(2, "0")}</span>
                    {pieceSrc(alt) ? <img src={pieceSrc(alt)} alt="" loading="lazy" className="h-10 w-10 object-cover border border-[#F5F2EA]/15 bg-[#F5F2EA]" />
                      : <span className="h-10 w-10 border border-[#F5F2EA]/15" style={{ backgroundColor: alt.color_hex }} />}
                    <span className="font-serif text-lg text-[#F5F2EA] flex-1">{alt.name}</span>
                    {alt.score != null && <span className="font-serif text-lg text-[#C8A97E]">{Number(alt.score).toFixed(1)}</span>}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* hidden share card for image export */}
      <div className="fixed -left-[9999px] top-0" aria-hidden>
        <ShareLookCard ref={cardRef} comp={active} score={score} occasion={data?.occasion} />
      </div>
    </div>
  );
}

function WornLook({ viz, stale, presentation, onUpdate, onToggle, onDownload }) {
  return (
    <div data-testid="worn-look">
      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-2">Worn Look Preview</p>
      <div className="relative aspect-[4/5] border border-[#F5F2EA]/10 bg-gradient-to-br from-[#141414] to-[#0F0F0F] overflow-hidden">
        {viz.status === "ready" && viz.image_url && (
          <>
            <img src={imageUrl(viz.image_url)} alt="MOIRÉ styled worn look" className="w-full h-full object-cover" data-testid="worn-look-image" />
            <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#0D0D0D]/70 backdrop-blur-sm font-sans text-[8px] tracking-luxe uppercase text-[#C8A97E]">Style Visualisation</span>
            {stale && (
              <div className="absolute inset-x-0 bottom-0 bg-[#0D0D0D]/85 backdrop-blur-sm p-3 flex items-center justify-between gap-3" data-testid="worn-stale">
                <span className="font-sans text-[10px] text-[#F5F2EA]/80">Based on your previous edit</span>
                <button onClick={onUpdate} data-testid="worn-update"
                  className="flex items-center gap-1.5 bg-[#C8A97E] text-[#0D0D0D] px-3 py-1.5 font-sans text-[10px] tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
                  <RefreshCw size={12} strokeWidth={1.8} /> Update Visualisation</button>
              </div>
            )}
          </>
        )}
        {viz.status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="worn-look-loading">
            <AnalyzingSequence stages={VIZ_STAGES} />
          </div>
        )}
        {(viz.status === "unavailable" || viz.status === "idle") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8" data-testid="worn-look-fallback">
            <MoireSymbol size={34} />
            <p className="font-serif italic text-lg text-[#F5F2EA]/70 mt-4 leading-relaxed">
              {viz.disclaimer || "Your complete styling board is ready below."}</p>
          </div>
        )}
      </div>

      {viz.status === "ready" && viz.image_url && (
        <>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2" data-testid="presentation-toggle">
              {[["masculine", "Masculine"], ["feminine", "Feminine"]].map(([key, label]) => (
                <button key={key} onClick={() => onToggle(key)} data-testid={`presentation-${key}`}
                  className={`font-sans text-[9px] tracking-luxe uppercase px-3 py-1.5 border transition-colors ${presentation === key ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}>
                  {label}</button>
              ))}
            </div>
            <button onClick={onDownload} data-testid="worn-download" className="flex items-center gap-1.5 font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E] transition-colors">
              <Download size={12} strokeWidth={1.6} /> Download</button>
          </div>
          <p className="font-sans text-[10px] text-[#8A8170] mt-2 italic leading-relaxed">
            {viz.disclaimer || "A style visualisation on a representative model — not an exact try-on."}</p>
        </>
      )}
    </div>
  );
}

function BoardTile({ item, big, kept, onSwap, onKeep }) {
  const src = pieceSrc(item);
  return (
    <div className={`relative group border border-[#F5F2EA]/10 overflow-hidden bg-[#F5F2EA] ${big ? "row-span-2" : ""}`} data-testid={`board-${item.slot}`}>
      {src
        ? <img src={src} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        : <div className="w-full h-full" style={{ backgroundColor: item.color_hex }} />}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0D0D0D] to-transparent p-2.5 pt-8">
        <p className="font-serif text-sm text-[#F5F2EA] leading-tight drop-shadow">{item.name}</p>
      </div>
      <button onClick={onKeep} title="Keep" className="absolute top-2 right-2 p-1.5 bg-[#0D0D0D]/60 backdrop-blur-sm">
        <Heart size={13} className={kept ? "text-[#C8A97E] fill-[#C8A97E]" : "text-[#F5F2EA]/80"} /></button>
      <button onClick={onSwap} data-testid={`board-swap-${item.slot}`} className="absolute top-2 left-2 px-2 py-1 bg-[#0D0D0D]/60 backdrop-blur-sm font-sans text-[9px] tracking-luxe uppercase text-[#C8A97E] opacity-0 group-hover:opacity-100 transition-opacity">Swap</button>
    </div>
  );
}

function AccTile({ item, kept, onSwap, onKeep, onRemove }) {
  const src = pieceSrc(item);
  return (
    <div className="relative group border border-[#F5F2EA]/12 flex items-center gap-2 pr-2" data-testid={`board-acc-${item.slot}`}>
      <button onClick={onSwap} className="flex items-center gap-2 p-1.5">
        <span className="h-9 w-9 border border-[#F5F2EA]/15 flex items-center justify-center overflow-hidden bg-[#F5F2EA]" style={!src ? { backgroundColor: item.color_hex } : {}}>
          {src && <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />}
        </span>
        <span className="font-sans text-[11px] text-[#F5F2EA]/85 max-w-[130px] truncate">{item.name}</span>
      </button>
      <button onClick={onKeep} title="Keep"><Heart size={12} className={kept ? "text-[#C8A97E] fill-[#C8A97E]" : "text-[#8A8170] hover:text-[#F5F2EA]"} /></button>
      {item.removable && <button onClick={onRemove} title="Remove" data-testid={`board-remove-${item.slot}`}><X size={12} className="text-[#8A8170] hover:text-[#F5F2EA]" /></button>}
    </div>
  );
}

export const ShareLookCard = React.forwardRef(({ comp, score, occasion }, ref) => (
  <div ref={ref} style={{ width: 500, background: "#0D0D0D", padding: 40, fontFamily: "Cormorant Garamond, serif" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
      <MoireSymbol size={30} />
      <span style={{ color: "#F5F2EA", letterSpacing: 6, fontSize: 20, fontFamily: "Manrope, sans-serif" }}>MOIRÉ</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
      {(comp?.garments || []).slice(0, 4).map((g, i) => (
        <div key={i} style={{ height: 100, background: g.color_hex, position: "relative", border: "1px solid rgba(245,242,234,.1)" }}>
          <span style={{ position: "absolute", bottom: 6, left: 8, color: "#F5F2EA", fontSize: 15, textShadow: "0 1px 4px #000" }}>{g.color_name}</span>
        </div>
      ))}
    </div>
    <div style={{ color: "#C8A97E", fontSize: 26 }}>{comp?.name}</div>
    <div style={{ color: "#C8A97E", fontSize: 44, lineHeight: 1 }}>{Number(score).toFixed(1)}<span style={{ color: "#8A8170", fontSize: 18 }}> / 10</span></div>
    <div style={{ color: "rgba(245,242,234,.8)", fontStyle: "italic", fontSize: 17, marginTop: 10 }}>{comp?.why}</div>
    <div style={{ marginTop: 22, borderTop: "1px solid rgba(245,242,234,.12)", paddingTop: 12, display: "flex", justifyContent: "space-between", fontFamily: "Manrope, sans-serif" }}>
      <span style={{ color: "#8A8170", fontSize: 10, letterSpacing: 3 }}>DRESS UP. DON'T MESS UP.</span>
      <span style={{ color: "#8A8170", fontSize: 10, letterSpacing: 3 }}>MOIRE.LIVE</span>
    </div>
  </div>
));
ShareLookCard.displayName = "ShareLookCard";
